/**
 * LockScreen — Tela de bloqueio unificada por inatividade (v2: Screensaver Animado).
 *
 * Layout Split-Screen:
 * - Esquerda (65%): Screensaver animado com publicações da região.
 * - Direita (35%): Controles de desbloqueio (Passkey, Google, Senha).
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { Lock, LogOut, LogIn, Loader2, Timer, Settings, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { trackEvent } from '../../api/observe';
import { audioAlerts } from '../../utils/audioAlerts';
import { AcMediaPlayer } from './AcMediaPlayer';
import { AcShieldLogo } from './AcShieldLogo';

const API = import.meta.env.VITE_API_URL || '/api/v1';
const UNLOCK_CHANNEL = 'ac_lock_broadcast';
const MAX_LOCK_MINUTES_BEFORE_LOGOUT = 60;

const SESSION_OPTIONS = [
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '1 hora', value: 60 },
];

interface Props {
  userName: string;
  userPhoto?: string | null;
  lockedAt: Date;
  passKeyAvailableUntil: Date;
  onUnlock: () => void;
  fingerprint: string;
  hasPasskey: boolean;
  passKeyDevices?: string[];
  isGoogleAccount?: boolean;
}

export function LockScreen({
  userName, userPhoto, lockedAt, passKeyAvailableUntil, onUnlock,
  fingerprint, hasPasskey, passKeyDevices = [], isGoogleAccount = false,
}: Props) {
  // --- Estados do LockScreen ---
  const [clock, setClock] = useState(new Date());
  const [elapsedStr, setElapsedStr] = useState('');
  const [remainingLockSeconds, setRemainingLockSeconds] = useState(
    MAX_LOCK_MINUTES_BEFORE_LOGOUT * 60,
  );
  const [phase, setPhase] = useState<'initial' | 'unlocking' | 'password'>('initial');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [passkeyAttempts, setPasskeyAttempts] = useState(0);
  const MAX_PASSKEY_ATTEMPTS = 3;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isMobile = useMemo(() => windowWidth < 768, [windowWidth]);

  /** Painel direito + inset do screensaver: proporcional à largura para não esmagar o conteúdo do player */
  const lockSidebarPx = useMemo(
    () => Math.min(420, Math.max(280, Math.round(windowWidth * 0.34))),
    [windowWidth],
  );

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const currentTimeout = (() => {
    try { const v = localStorage.getItem('ac_session_timeout'); return v ? parseInt(v) : 5; } catch { return 5; }
  })();
  const [sessionTimeout, setSessionTimeout] = useState(currentTimeout);

  const senhaRef = useRef<HTMLInputElement>(null);
  const forcedLogoutRef = useRef(false);
  const navigate = useNavigate();
  const logout = useAuthStore(s => s.logout);
  const token = localStorage.getItem('ac_token') || '';
  const userId = useAuthStore(s => s.user?.id) || '';
  const userEmail = useAuthStore(s => s.user?.email) || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const initials = userName.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  // Mount effects
  useEffect(() => {
    trackEvent({ source: 'lock_screen', action: 'viewed', targetId: userId, targetType: 'usuario' });
    audioAlerts.playLock();
  }, []);

  // Broadcast Channel
  useEffect(() => {
    const bc = new BroadcastChannel(UNLOCK_CHANNEL);
    bc.onmessage = (ev) => { if (ev.data?.type === 'unlock') onUnlock(); };
    return () => bc.close();
  }, [onUnlock]);

  // Relógio
  useEffect(() => {
    const iv = setInterval(() => {
      const now = new Date();
      setClock(now);
      const diffMs = now.getTime() - lockedAt.getTime();
      const secs = Math.floor(diffMs / 1000);
      const remaining = Math.max(
        0,
        MAX_LOCK_MINUTES_BEFORE_LOGOUT * 60 - secs,
      );
      const mins = Math.floor(secs / 60);
      const hrs = Math.floor(mins / 60);
      if (hrs > 0) setElapsedStr(`${hrs}h ${mins % 60}m`);
      else if (mins > 0) setElapsedStr(`${mins}m ${secs % 60}s`);
      else setElapsedStr(`${secs}s`);
      setRemainingLockSeconds(remaining);

      if (remaining <= 0 && !forcedLogoutRef.current) {
        forcedLogoutRef.current = true;
        setError('Sessão encerrada por segurança após 30 minutos bloqueada.');
        trackEvent({
          source: 'lock_screen',
          action: 'auto_logout_timeout',
          targetId: userId,
          targetType: 'usuario',
        });
        audioAlerts.playDisconnect();
        logout();
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [lockedAt, userId, logout]);

  useEffect(() => {
    if (phase === 'password') setTimeout(() => senhaRef.current?.focus(), 100);
  }, [phase]);

  function completeUnlock(method: string) {
    if (sessionTimeout !== currentTimeout) {
      localStorage.setItem('ac_session_timeout', String(sessionTimeout));
      fetch(`${API}/auth/me/preferencias`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ timeoutBloqueioMinutos: sessionTimeout }),
      }).catch(() => {});
    }
    trackEvent({ source: 'lock_screen', action: `unlock_${method}`, targetId: userId, targetType: 'usuario' });
    audioAlerts.playUnlock();
    try { const bc = new BroadcastChannel(UNLOCK_CHANNEL); bc.postMessage({ type: 'unlock' }); bc.close(); } catch {}
    onUnlock();
  }

  async function handleAcessar() {
    setError('');
    setPhase('password');
  }

  async function doGoogleLogin(): Promise<boolean> {
    const { initializeApp, getApps } = await import('firebase/app');
    const firebaseAuth = await import('firebase/auth');
    const cfg = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    };
    const app = getApps().length ? getApps()[0] : initializeApp(cfg);
    const auth = firebaseAuth.getAuth(app);
    const provider = new firebaseAuth.GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await firebaseAuth.signInWithPopup(auth, provider);
    const firebaseToken = await result.user.getIdToken();
    const res = await fetch(`${API}/auth/login-google`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firebaseToken }),
    });
    if (res.ok) {
      const data = await res.json();
      const returnedEmail = (data?.user?.email || '').toLowerCase();
      if (returnedEmail && userEmail && returnedEmail !== userEmail.toLowerCase()) {
        setError('Esta não é a conta correta para desbloquear esta sessão.');
        return false;
      }
      if (data.access_token) localStorage.setItem('ac_token', data.access_token);
      return true;
    }
    return false;
  }

  async function handlePasswordUnlock(e: React.FormEvent) {
    e.preventDefault();
    if (senha.length < 6) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, senha }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.access_token) localStorage.setItem('ac_token', data.access_token);
        setSenha('');
        completeUnlock('password');
        return;
      }
      const data = await res.json().catch(() => ({}));
      setError(data.message || 'Senha incorreta.');
    } catch { setError('Erro ao verificar senha.'); }
    setLoading(false);
  }

  function handleDisconnect() {
    trackEvent({ source: 'lock_screen', action: 'disconnected', targetId: userId, targetType: 'usuario' });
    audioAlerts.playDisconnect();
    logout();
  }

  const senhaOk = senha.length >= 6;
  const remainingLabel =
    remainingLockSeconds > 0
      ? `${Math.floor(remainingLockSeconds / 60)}m ${remainingLockSeconds % 60}s`
      : '0m 0s';

  // ═══════════════════════════════════════════════════
  // MOBILE: Player fullscreen + botões compactos embaixo
  // ═══════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999, background: '#020617',
        display: 'flex', flexDirection: 'column', minHeight: '100dvh',
      }}
      >
        {/* Player ocupa o espaço restante; minHeight:0 evita comer a barra de botões */}
        <div style={{ flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          <AcMediaPlayer mode="compact" propagandaInterval={5} />
        </div>

        {/* Barra compacta de ações na base */}
        <div style={{
          flexShrink: 0, flexGrow: 0,
          padding: '12px 16px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          background: 'rgba(15,23,42,0.95)',
          borderTop: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {error && <div style={{ color: '#f87171', fontSize: '0.8rem', textAlign: 'center' }}>{error}</div>}

          {phase === 'password' ? (
            <form onSubmit={handlePasswordUnlock} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                ref={senhaRef} type="password" placeholder="Digite sua senha" value={senha}
                onChange={e => setSenha(e.target.value)} autoFocus
                style={{ ...inputStyle, padding: '12px 14px', fontSize: '0.88rem', borderRadius: 12 }}
                onFocus={e => { e.currentTarget.style.borderColor = '#6366f1'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={handleDisconnect} style={{
                  ...btnBase, flex: '0 0 auto', background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', padding: '12px 18px',
                }}>
                  <LogOut size={16} />
                </button>
                <button type="submit" disabled={!senhaOk || loading} style={{
                  ...btnBase, flex: 1,
                  background: senhaOk ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255,255,255,0.06)',
                  color: '#fff', padding: '12px 20px', opacity: senhaOk ? 1 : 0.5,
                  cursor: senhaOk ? 'pointer' : 'not-allowed',
                }}>
                  {loading ? <><Loader2 size={16} style={{ animation: 'lk-spin 1s linear infinite' }} /> Verificando...</> : 'Desbloquear'}
                </button>
              </div>
            </form>
          ) : phase === 'unlocking' && loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '8px 0' }}>
              <Loader2 size={22} color="#f97316" style={{ animation: 'lk-spin 1s linear infinite' }} />
              <span style={{ color: '#94a3b8', fontSize: '.85rem', fontWeight: 600 }}>Verificando...</span>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              <button onClick={handleDisconnect} style={{
                ...btnBase, flex: 1, background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', padding: '13px 20px',
                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8
              }}>
                <LogOut size={16} /> Sair
              </button>
              <button onClick={handleAcessar} style={{
                ...btnBase, flex: 1, background: 'linear-gradient(135deg, #f97316, #ea580c)',
                color: '#fff', padding: '13px 20px', boxShadow: '0 4px 16px rgba(249,115,22,0.3)',
                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8
              }}>
                Entrar <LogIn size={16} />
              </button>
            </div>
          )}
        </div>

        <style>{`
          @keyframes lk-spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // Usuários armazenados (multi-account)
  // ═══════════════════════════════════════════════════
  const storedUsers = useMemo(() => {
    try {
      const raw = localStorage.getItem('ac_stored_users');
      if (!raw) return [];
      const list = JSON.parse(raw) as Array<{ id: string; nome: string; email: string; foto?: string }>;
      return list.filter(u => u.id !== userId);
    } catch { return []; }
  }, [userId]);

  const firstName = userName.split(' ')[0];

  // ═══════════════════════════════════════════════════
  // DESKTOP / TABLET: Overlay-screen (Player ao fundo + Sidebar de Vidro)
  // ═══════════════════════════════════════════════════
  return (
    <div style={containerStyle}>
      {/* ─── PAINEL ESQUERDO/FUNDO: Ac Media Engine ─── */}
      <div style={screensaverPanel}>
        <AcMediaPlayer mode="full" sidebarWidth={lockSidebarPx} propagandaInterval={5} />
      </div>

      {/* ─── PAINEL DIREITO: Sidebar Premium de Vidro ─── */}
      <div style={{
        ...controlPanel,
        width: lockSidebarPx,
        minHeight: 0,
        maxHeight: '100dvh',
        overflow: 'hidden',
        padding: '0 12px',
        paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
      }}
      >

        {/* ── Topo: marca compacta ── */}
        <div style={{
          flexShrink: 0,
          width: 'calc(100% + 24px)',
          margin: '0 -12px 12px -12px',
          padding: '10px 12px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          animation: 'acm-slideUp 0.6s ease both',
        }}
        >
          <AcShieldLogo style={{ width: 40, height: 40, flexShrink: 0 }} />
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 1 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 900, color: '#f8fafc', letterSpacing: '.14em', lineHeight: 1 }}>ALUNO</span>
            <span style={{
              fontSize: '0.72rem',
              fontWeight: 900,
              color: '#9333ea',
              letterSpacing: '.14em',
              lineHeight: 1,
              textShadow: '0 1px 8px rgba(147, 51, 234, 0.45)',
            }}
            >
              CONECTADO
            </span>
          </div>
        </div>

        {/* ── Centro: scroll + bloco centrado verticalmente quando há espaço livre ── */}
        <div style={{
          flex: '1 1 auto',
          minHeight: 0,
          width: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
          animation: 'acm-slideUp 0.7s ease both',
          animationDelay: '0.1s',
        }}
        >
          <div style={{
            marginTop: 'auto',
            marginBottom: 'auto',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 8,
            paddingBottom: 8,
            boxSizing: 'border-box',
          }}
          >
          
          {/* Avatar compacto */}
          <div style={{ position: 'relative', width: 76, height: 76, marginBottom: 8 }}>
            {/* Anel */}
            <div style={{ position: 'absolute', inset: -4, borderRadius: '50%', background: 'conic-gradient(from 0deg, rgba(245,158,11,0) 0%, rgba(245,158,11,0.45) 50%, rgba(245,158,11,0) 100%)', animation: 'lk-spin 4s linear infinite', zIndex: 0 }} />

            <div style={{
              position: 'relative', zIndex: 1, width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden',
              boxShadow: '0 10px 24px rgba(0,0,0,0.55), inset 0 1px 2px rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.14)',
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            >
              {userPhoto ? (
                <img src={userPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: '1.65rem', fontWeight: 600, color: '#f8fafc', letterSpacing: '-0.03em' }}>
                  {firstName.substring(0, 2).toUpperCase()}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowConfig(!showConfig)}
              style={{
                position: 'absolute', bottom: -1, right: -1, zIndex: 2, width: 28, height: 28, borderRadius: '50%',
                background: 'rgba(15,23,42,0.92)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.18)',
                color: showConfig ? '#a78bfa' : '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.45)', transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.color = '#fff'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.color = showConfig ? '#a78bfa' : '#cbd5e1'; }}
            >
              <Settings size={13} strokeWidth={2.25} />
            </button>

            {showConfig && (
              <div style={{ ...configDropdown, top: 34, right: -168, zIndex: 10, width: 200, padding: '12px 14px', animation: 'acm-slideUp 0.3s ease both' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: '.68rem', fontWeight: 700, color: '#94a3b8' }}>
                    <Timer size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
                    Tempo de bloqueio
                  </span>
                  <button onClick={() => setShowConfig(false)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 2 }}>
                    <X size={14} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  {SESSION_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setSessionTimeout(opt.value); setShowConfig(false); }}
                      style={{
                        flex: 1, padding: '7px 3px', borderRadius: 8, fontSize: '.7rem', fontWeight: 700,
                        border: sessionTimeout === opt.value ? '1.5px solid #a78bfa' : '1px solid rgba(255,255,255,0.08)',
                        background: sessionTimeout === opt.value ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.03)',
                        color: sessionTimeout === opt.value ? '#c4b5fd' : '#64748b', cursor: 'pointer', transition: 'all .15s',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <h2 style={{
            margin: 0,
            fontSize: 'clamp(1.05rem, 4.2vmin, 1.35rem)',
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '-0.02em',
            marginBottom: 10,
            textShadow: '0 2px 12px rgba(0,0,0,0.45)',
            textAlign: 'center',
            width: '100%',
          }}
          >
            {firstName}
          </h2>

          <div style={{
            background: 'rgba(0, 0, 0, 0.38)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.09)', borderRadius: 100, padding: '3px 10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', rowGap: 6, columnGap: 10,
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)', marginBottom: 10, maxWidth: '100%', alignSelf: 'center',
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#cbd5e1', fontSize: '0.68rem', fontWeight: 500 }}>
              <Lock size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
              <span style={{ whiteSpace: 'nowrap' }}>Bloqueada às {lockedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>

            <div style={{
              background: 'rgba(245, 158, 11, 0.14)', border: '1px solid rgba(245, 158, 11, 0.28)',
              padding: '3px 10px', borderRadius: 100, display: 'flex', alignItems: 'center', gap: 5,
              color: '#fbbf24', fontWeight: 700, fontSize: '0.72rem', boxShadow: 'inset 0 0 8px rgba(245, 158, 11, 0.08)',
            }}
            >
              <Timer size={15} strokeWidth={2.25} />
              <span style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' }}>{elapsedStr}</span>
            </div>
          </div>
          <p style={{ margin: '0 0 10px', fontSize: '.62rem', color: '#fbbf24', fontWeight: 600, textAlign: 'center', lineHeight: 1.35 }}>
            Desconexão automática em {remainingLabel}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <span style={{
              fontSize: 'clamp(1.65rem, 6.5vmin, 2.35rem)',
              fontWeight: 300,
              color: '#ffffff',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.03em',
              lineHeight: 1,
              textShadow: '0 6px 24px rgba(0,0,0,0.45)',
            }}
            >
              {clock.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <div style={{
              fontSize: '0.58rem',
              fontWeight: 600,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              marginTop: 4,
              textAlign: 'center',
              lineHeight: 1.35,
            }}
            >
              {clock.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }).split(', ').map((part, index, arr) => (
                <span key={index}>
                  {part}
                  {index < arr.length - 1 && <br />}
                </span>
              ))}
            </div>
          </div>
          </div>
        </div>

        {/* ── Base: sempre visível (não depende só de margin-top:auto com overflow pai) ── */}
        <div style={{
          flexShrink: 0,
          flexGrow: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginTop: 'auto',
          animation: 'acm-slideUp 0.8s ease both',
          animationDelay: '0.3s',
        }}
        >
          {error && <div style={{ color: '#f87171', fontSize: '0.68rem', textAlign: 'center', fontWeight: 600 }}>{error}</div>}

          {/* Fase 1 — mesmo critério do mobile: evita “buraco” se unlocking ficar sem loading um instante */}
          {(phase === 'initial' || (phase === 'unlocking' && !loading)) && (
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              <button type="button" onClick={handleDisconnect} style={{
                ...btnBase, flex: 1, background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1', padding: '10px 12px',
                fontSize: '0.78rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, borderRadius: 11,
              }}
              >
                <LogOut size={15} /> Sair
              </button>
              <button type="button" onClick={handleAcessar} style={{
                ...btnBase, flex: 1, background: 'linear-gradient(135deg, #f97316, #ea580c)',
                color: '#fff', padding: '10px 12px', boxShadow: '0 4px 16px rgba(249,115,22,0.28)',
                fontSize: '0.78rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, borderRadius: 11,
              }}
              >
                Entrar <LogIn size={15} />
              </button>
            </div>
          )}

          {phase === 'unlocking' && loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%', padding: '10px 0' }}>
              <Loader2 size={22} color="#f97316" style={{ animation: 'lk-spin 1s linear infinite' }} />
              <span style={{ color: '#94a3b8', fontSize: '.72rem', fontWeight: 600 }}>Verificando identidade...</span>
            </div>
          )}

          {phase === 'password' && (
            <form onSubmit={handlePasswordUnlock} style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
              <input
                ref={senhaRef} type="password" placeholder="Digite sua senha" value={senha} onChange={e => setSenha(e.target.value)} autoFocus
                style={{ ...inputStyle, padding: '10px 12px', fontSize: '0.8rem', borderRadius: 11 }}
                onFocus={e => { e.currentTarget.style.borderColor = '#6366f1'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={handleDisconnect} style={{
                  ...btnBase, flex: '0 0 auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', padding: '10px 12px', borderRadius: 11,
                }}
                >
                  <LogOut size={15} />
                </button>
                <button
                  type="submit" disabled={!senhaOk || loading}
                  style={{
                    ...btnBase, flex: 1,
                    background: senhaOk ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255,255,255,0.06)',
                    color: '#fff', padding: '10px 12px', opacity: senhaOk ? 1 : 0.5,
                    cursor: senhaOk ? 'pointer' : 'not-allowed', fontSize: '0.78rem', borderRadius: 11,
                  }}
                >
                  {loading ? <><Loader2 size={15} style={{ animation: 'lk-spin 1s linear infinite' }} /> Verificando...</> : 'Desbloquear'}
                </button>
              </div>
              {(hasPasskey || isGoogleAccount) && (
                <button type="button" onClick={() => { setPhase('initial'); setError(''); setSenha(''); }} style={{
                  background: 'none', border: 'none', color: '#94a3b8', fontSize: '.68rem', cursor: 'pointer', padding: '4px 0', textAlign: 'center', fontWeight: 600, transition: 'color .2s',
                }} onMouseEnter={e => e.currentTarget.style.color = '#fff'} onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}>
                  {hasPasskey ? 'Tentar Passkey novamente' : 'Tentar Google novamente'}
                </button>
              )}
            </form>
          )}

          {storedUsers.length > 0 && (
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10, marginTop: 4 }}>
              <p style={{ fontSize: '.6rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8, textAlign: 'center' }}>
                Trocar Conta
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {storedUsers.map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => {
                      setError('');
                      console.log('Switch to user:', u.email);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '8px 10px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      color: '#cbd5e1', cursor: 'pointer', transition: 'all .2s', fontSize: '.74rem',
                      fontWeight: 700,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                  >
                    {u.foto ? (
                      <img src={u.foto} alt="" style={{ width: 26, height: 26, borderRadius: 7, objectFit: 'cover' }} />
                    ) : (
                      <div style={{
                        width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'linear-gradient(135deg, #334155, #475569)', fontSize: '.68rem', fontWeight: 900, color: '#f8fafc',
                      }}>{u.nome.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}</div>
                    )}
                    <span>{u.nome.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes lk-spin { to { transform: rotate(360deg); } }
        @keyframes acm-slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes acm-slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
        @keyframes bgPan { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
      `}</style>
    </div>
  );
}

// ── Styles ──
const containerStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 99999, background: '#020617',
};

// Left Panel (Screensaver) — Player preenche tudo
const screensaverPanel: React.CSSProperties = {
  position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden',
};
const screensaverBg: React.CSSProperties = {
  position: 'absolute', inset: 0,
  background: 'linear-gradient(120deg, #1e3a8a, #0f172a, #111827, #312e81)',
  backgroundSize: '400% 400%', animation: 'bgPan 25s ease infinite', zIndex: 1,
};
const bgVideoStyle: React.CSSProperties = {
  position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 2, opacity: 0.6,
};
const bgImageStyle: React.CSSProperties = {
  position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 2, opacity: 0.6,
};
const contentOverlayGradient: React.CSSProperties = {
  position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(2,6,23,0.95) 0%, rgba(2,6,23,0) 60%)', zIndex: 3,
};
const dynamicContentContainer: React.CSSProperties = {
  position: 'relative', zIndex: 4, maxWidth: '80%', color: '#fff',
};
const slideAnimation: React.CSSProperties = {
  animation: 'slideInUp 0.8s ease-out forwards',
};
const titleStyle: React.CSSProperties = {
  fontSize: '3.5rem', fontWeight: 800, lineHeight: 1.1, marginBottom: 16, letterSpacing: '-0.02em',
};
const subtitleStyle: React.CSSProperties = {
  fontSize: '1.5rem', fontWeight: 400, color: '#94a3b8', marginBottom: 24,
};
const htmlContentStyle: React.CSSProperties = {
  fontSize: '1.1rem', color: '#cbd5e1', lineHeight: 1.6, maxWidth: 600,
};
const defaultScreensaver: React.CSSProperties = {
  display: 'flex', flexDirection: 'column',
};
const logoIconPlaceholder: React.CSSProperties = {
  width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #3b82f6, #1e40af)', marginBottom: 8,
};
const soundToggleBtn: React.CSSProperties = {
  position: 'absolute', top: 32, left: 32, zIndex: 5, background: 'rgba(2,6,23,0.5)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#fff', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', backdropFilter: 'blur(10px)',
};
const protectionOverlay: React.CSSProperties = {
  position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(2,6,23,0.95)', backdropFilter: 'blur(20px)',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  color: '#fff', transition: 'opacity 0.6s ease',
};

// Right Panel (Controls) — Premium de Vidro com animação de slide-in
const controlPanel: React.CSSProperties = {
  position: 'absolute', top: 0, right: 0, bottom: 0, width: 420, zIndex: 2,
  background: 'linear-gradient(180deg, rgba(15,23,42,0.6) 0%, rgba(2,6,23,0.95) 100%)',
  backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)',
  borderLeft: '1px solid rgba(255,255,255,0.06)',
  display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 12px 12px 12px',
  boxShadow: '-20px 0 60px rgba(0,0,0,0.6)',
  animation: 'acm-slideInRight 0.8s cubic-bezier(0.16, 1, 0.3, 1) both',
};
const configDropdown: React.CSSProperties = {
  position: 'absolute', background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 14, padding: '16px 18px', minWidth: 220, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 100,
};
const avatarWrap: React.CSSProperties = {
  width: 100, height: 100, borderRadius: '50%', border: '3px solid rgba(59,130,246,0.3)', overflow: 'hidden', boxShadow: '0 0 30px rgba(59,130,246,0.15)',
};
const initialsCircle: React.CSSProperties = {
  width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'linear-gradient(135deg, #1e40af, #3b82f6)', color: '#fff', fontSize: '2rem', fontWeight: 800,
};
const btnBase: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 14, border: 'none',
  fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
};
const infoBoxStyle: React.CSSProperties = {
  padding: '10px 20px', borderRadius: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 28, fontSize: '0.78rem', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center',
};
const timerPillStyle: React.CSSProperties = {
  background: 'rgba(245,158,11,0.15)', padding: '2px 8px', borderRadius: 6, fontWeight: 700, fontSize: '0.74rem', fontFamily: 'monospace',
};
const inputStyle: React.CSSProperties = {
  padding: '15px 18px', borderRadius: 14, border: '1.5px solid rgba(255,255,255,0.15)', background: '#ffffff', color: '#0f172a', fontSize: '0.92rem', fontWeight: 600, outline: 'none', width: '100%', transition: 'border-color .2s',
};
